import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, ArrowLeft, Package, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import moduleService from '../services/moduleService';
import partService from '../services/partService';
import toast from 'react-hot-toast';
import PartTable from './PartTable';
import PartFormModal from './PartFormModal';
import ConfirmModal from './ConfirmModal';

const ModuleParts = () => {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();

  const [module, setModule] = useState(null);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [editingPart, setEditingPart] = useState(null);

  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);

  const canAccess = hasRole?.(['GERENCIA']) || hasRole?.(['RECEPCAO_COMPRAS_AGENDA']);

  const loadData = async () => {
    if (!canAccess) {
      setError('Você não tem permissão para acessar este recurso');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const moduleResponse = await moduleService.getModuleById(moduleId);
      if (moduleResponse.success && moduleResponse.data) {
        setModule(moduleResponse.data);

        const partsResponse = await partService.getPartsByModule(moduleId);
        if (partsResponse.success && Array.isArray(partsResponse.data)) {
          setParts(partsResponse.data);
        } else {
          setParts([]);
        }
      } else {
        setError('Módulo não encontrado');
      }
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError('Erro ao carregar dados');
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [moduleId, canAccess]);

  const handleCreate = () => {
    setEditingPart(null);
    setShowForm(true);
  };

  const handleEdit = (part) => {
    setEditingPart(part);
    setShowForm(true);
  };

  const handleDelete = (part) => {
    setConfirmMessage(`Tem certeza que deseja excluir a peça ${part.part_code}?`);
    setPendingDelete(() => async () => {
      try {
        await partService.deletePart(part.id);
        toast.success('Peça excluída com sucesso');
        loadData();
      } catch (err) {
        console.error('Erro ao deletar peça:', err);
        toast.error('Erro ao deletar peça');
      }
    });
    setShowConfirm(true);
  };

  const handleConfirm = () => {
    if (pendingDelete) {
      pendingDelete();
    }
    setShowConfirm(false);
    setPendingDelete(null);
  };

  const handleCancelConfirm = () => {
    setShowConfirm(false);
    setPendingDelete(null);
  };

  const handleSave = async (partId, data) => {
    try {
      if (partId) {
        await partService.updatePart(partId, data);
        toast.success('Peça atualizada com sucesso');
      } else {
        await partService.createPart(moduleId, data);
        toast.success('Peça criada com sucesso');
      }
      loadData();
    } catch (err) {
      console.error('Erro ao salvar peça:', err);
      toast.error(err.message || 'Erro ao salvar peça');
      throw err;
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-16 bg-gray-200 rounded-lg"></div>
          <div className="h-64 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-center gap-3">
          <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
          <div>
            <p className="text-red-800 font-medium">{error}</p>
            <button
              onClick={() => navigate(-1)}
              className="text-red-600 hover:text-red-800 text-sm mt-1"
            >
              Voltar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <nav className="flex items-center gap-2 text-sm text-gray-600 mb-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar
        </button>
        <span>/</span>
        <span className="text-gray-900 font-medium">{module?.module_code}</span>
        <span>/</span>
        <span className="text-gray-500">Peças</span>
      </nav>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Peças do Módulo</h1>
          <p className="text-gray-600 mt-1">
            {module?.module_code} - {module?.module_name} - {parts.length} peça(s)
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nova Peça
        </button>
      </div>

      <PartTable
        parts={parts}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <PartFormModal
        part={editingPart}
        moduleId={moduleId}
        moduleProjectId={module?.project_id}
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        onSave={handleSave}
      />

      <ConfirmModal
        isOpen={showConfirm}
        message={confirmMessage}
        onConfirm={handleConfirm}
        onCancel={handleCancelConfirm}
      />
    </div>
  );
};

export default ModuleParts;
